import { clearMessages } from '~/lib/indexedDb/inbox';
// --- internal imports ------

/**
 * Everything on this machine that belongs to one playthrough.
 *
 * Dropped when a player deliberately restarts. NOT dropped on a logout: a 401 is
 * often just an expired token, and the player who signs back in expects to land
 * in the game they left. See `clearSessionData` below.
 *
 * STANDING INSTRUCTION: any localStorage field added to this codebase, or changed
 * in it, must be checked against this list. If it belongs to a playthrough, it
 * goes here.
 *
 * Deliberately *not* here:
 *   `<gameUserId>:*TutorialStep` — namespaced by game user, so it cannot leak
 *       across players, and it is the progress that should survive a restart.
 *   `areHintsDisabled` — a player preference, not part of a playthrough.
 *   `dev_tools_*` — developer machine settings, never set for a player.
 */
export const GAMEPLAY_STORAGE_KEYS = [
	'current-game-id',
	'store_id',
	'character_id',
	'screen',
	'automatedEmployees',
	'gameUsersForThisUser',
	'gamePlayTime',
	'totalPlayTime',
	'shouldRefreshTurnTimer',
	'dontShowMissoutOnCustomersModal',
	'dontShowConfirmationForTurnPhase'
];

// Held only while the player is signed in.
export const AUTH_STORAGE_KEYS = [
	'tradersToken'
];

/**
 * Drops everything belonging to the playthrough, including the messages held in
 * IndexedDB — those are keyed by message id alone, with no game or player in the
 * key, so anything left behind is read back by the next game on this machine.
 *
 * Restart only. A logout goes through `clearSessionData`, which does not call
 * this.
 */
export async function clearGameplayData() {
	GAMEPLAY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

	try {
		await clearMessages();
	}
	catch (error) {
		// A blocked or failed IndexedDB transaction must not stop a restart.
		console.error('Failed to clear the inbox messages.', error);
	}
}

/**
 * Drops the signed-in session and nothing else.
 *
 * The playthrough stays on disk on purpose. The 401 that brings us here is not
 * always a real logout — an expired or briefly rejected token raises one too —
 * and taking the save with it would cost the player their game for what is
 * usually a re-login. Whoever signs in next is scoped by the server: the game id
 * left behind is sent as `X-Game-User-id` and rejected if it is not theirs, and
 * a restart still clears the lot through `clearGameplayData`.
 */
export function clearSessionData() {
	AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}
